import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Maximize2, Minus, Plus } from "lucide-react";
import type {
  FishboneBranch,
  FishboneParams,
} from "@playbookai/widget-schemas";
import { Tooltip } from "../../molecules/index.js";
import { FloatingToolbar } from "../../canvas/FloatingToolbar.js";
import { ToolbarIconButton } from "../../canvas/toolbar-primitives.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * Fishbone (Ishikawa) — Look, singleton. The classic fish skeleton: a spine
 * with an arrowhead into an amber Effect head, category ribs alternating
 * above/below at 52°, and cause leaves along each rib. Ported from the oracle
 * FishboneCard SVG (workspace/look/FishboneCard.tsx).
 *
 * Category hues use DS tokens (LK-13 ruling = keep DS tokens, not the oracle's
 * raw rgb pink/indigo). Display-only: editing routes through the expanded
 * editor, not this data-blind body.
 */
interface Palette {
  stroke: string;
  fill: string;
  text: string;
}

const ds = (channel: string): Palette => ({
  stroke: `hsl(var(--pb-${channel}))`,
  fill: `hsl(var(--pb-${channel}) / 0.10)`,
  text: `hsl(var(--pb-${channel}))`,
});

// DS-token palette per 6M category (matches the app's existing grid mapping).
const KNOWN_PALETTE: Record<string, Palette> = {
  Man: ds("cyan"),
  Method: ds("amber"),
  Machine: ds("purple"),
  Material: ds("teal"),
  Measurement: ds("red"),
  Environment: ds("purple"),
};
const FALLBACK_PALETTES: Palette[] = [
  ds("teal"),
  ds("cyan"),
  ds("amber"),
  ds("red"),
];

function paletteFor(category: string, index: number): Palette {
  return (
    KNOWN_PALETTE[category] ??
    FALLBACK_PALETTES[index % FALLBACK_PALETTES.length]!
  );
}

/* ----------------------------- layout ----------------------------------- */

const RIB_ANGLE_DEG = 52;
const RIB_RAD = (RIB_ANGLE_DEG * Math.PI) / 180;
const RIB_COS = Math.cos(RIB_RAD);
const RIB_SIN = Math.sin(RIB_RAD);
const LEAF_LEN = 22;
const LABEL_PAD = 4;
const LABEL_LINE_H = 13;
const PILL_HALF_W = 64;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.1;
const WHEEL_ZOOM_PER_PIXEL = 0.0007;
const MAX_WHEEL_PIXELS = 100;

interface DiagramViewport {
  zoom: number;
  panX: number;
  panY: number;
}

const FIT_VIEWPORT: DiagramViewport = { zoom: 1, panX: 0, panY: 0 };

function roundZoom(value: number): number {
  return Math.round(value * 10) / 10;
}

function clampViewport(
  viewport: DiagramViewport,
  layout: Pick<FishboneLayout, "vbW" | "vbH">,
): DiagramViewport {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, viewport.zoom));
  const visibleWidth = layout.vbW / zoom;
  const visibleHeight = layout.vbH / zoom;
  const maxPanX = Math.max(0, (layout.vbW - visibleWidth) / 2);
  const maxPanY = Math.max(0, (layout.vbH - visibleHeight) / 2);

  return {
    zoom,
    panX: Math.min(maxPanX, Math.max(-maxPanX, viewport.panX)),
    panY: Math.min(maxPanY, Math.max(-maxPanY, viewport.panY)),
  };
}

/**
 * Average advance width for a wrapped label, in px per character.
 *
 * 6.2px ≈ 0.59em at the label's 10.5px. An earlier cut raised this and added a
 * spare line, on the theory that the printed report's overlapping labels came
 * from under-counting. They did not: the capture was rendering those labels at
 * 16px because the cloned document dropped their `font` shorthand (see
 * `report/capture-styles.ts`). Padding the estimate made the canvas diagram
 * airier to compensate for a fault that was never in the geometry, so it is
 * reverted — the live widget is correct, and the capture now matches it.
 */
const LABEL_CHAR_W_WRAP = 6.2;

function estimateLines(text: string, labelW: number): number {
  const usable = Math.max(LABEL_CHAR_W_WRAP, labelW - 6);
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 1;
  let lines = 1;
  let currentWidth = 0;
  for (const word of words) {
    const wordWidth = word.length * LABEL_CHAR_W_WRAP;
    const spaceWidth = currentWidth > 0 ? LABEL_CHAR_W_WRAP : 0;
    if (currentWidth + spaceWidth + wordWidth <= usable) {
      currentWidth += spaceWidth + wordWidth;
    } else if (wordWidth <= usable) {
      lines += 1;
      currentWidth = wordWidth;
    } else {
      if (currentWidth > 0) lines += 1;
      const wrappedLines = Math.ceil(wordWidth / usable);
      lines += wrappedLines - 1;
      currentWidth = wordWidth - (wrappedLines - 1) * usable;
    }
  }
  return lines;
}

interface LaidOutLeaf {
  cause: FishboneBranch["causes"][number];
  lx: number;
  ly: number;
  labelH: number;
}

interface LaidOutBranch extends FishboneBranch {
  side: "above" | "below";
  ribBaseX: number;
  ribLen: number;
  ribEndX: number;
  ribEndY: number;
  palette: Palette;
  leaves: LaidOutLeaf[];
}

export interface FishboneLayout {
  branches: LaidOutBranch[];
  spineX1: number;
  spineX2: number;
  spineY: number;
  vbW: number;
  vbH: number;
  headW: number;
  headH: number;
  labelW: number;
  truncate: boolean;
}

/** Pure geometry: spine length, alternating ribs, head box. */
export function computeLayout(
  branches: FishboneBranch[],
  compact: boolean,
): FishboneLayout {
  const labelW = compact ? 96 : 140;
  const truncate = compact;
  const headW = compact ? 132 : 168;
  const headH = compact ? 64 : 80;

  const leafGap = compact ? 8 : 10;
  const ribInfo = branches.map((branch) => {
    const labelHs = branch.causes.map(
      (cause) =>
        (compact ? 1 : estimateLines(cause.text, labelW)) * LABEL_LINE_H + 5,
    );
    const maxLabelH = labelHs.length ? Math.max(...labelHs) : LABEL_LINE_H + 5;
    const perLeafHeight = maxLabelH + leafGap;
    const ribLen = Math.max(
      compact ? 96 : 110,
      ((branch.causes.length + 1) * perLeafHeight) / RIB_SIN,
    );
    return { labelHs, ribLen };
  });
  const maxRibLen = ribInfo.length
    ? Math.max(120, ...ribInfo.map(({ ribLen }) => ribLen))
    : 120;
  const ribYExt = RIB_SIN * maxRibLen;

  const labelFootprint = LEAF_LEN + LABEL_PAD + labelW;
  const slotStep = Math.max(
    compact ? 96 : 110,
    (labelFootprint + PILL_HALF_W + 24) / 2,
  );

  const SPINE_X1 = 48;
  /* Half-height measured from the spine to the furthest thing actually drawn on
     either side, rather than a fixed pad past the rib:
       · a branch's badge sits 4px past its rib end and is 20px tall;
       · a leaf's label is centred on its point on the rib, so the LAST leaf
         reaches `RIB_SIN·ribLen·count/(count+1) + labelH/2`, which for a tall
         wrapped label can exceed the rib's own vertical extent.
     The old `2·(ribYExt + 44) + 8` happened to contain both for the shapes
     tested, so this is not a bug fix — it is the same number derived from what
     is drawn instead of from a constant, which is what keeps it true when a
     label's height changes. */
  const badgeExtent = ribYExt + 4 + 20;
  const labelExtent = ribInfo.reduce((furthest, { ribLen, labelHs }, index) => {
    const count = branches[index]?.causes.length ?? 0;
    if (count === 0) return furthest;
    const lastOffset = (RIB_SIN * ribLen * count) / (count + 1);
    const tallest = Math.max(...labelHs);
    return Math.max(furthest, lastOffset + tallest / 2);
  }, 0);
  const halfHeight = Math.max(badgeExtent, labelExtent) + 12;
  const vbH = 2 * halfHeight;
  const spineY = vbH / 2;

  const out: LaidOutBranch[] = branches.map((b, i) => {
    const side: "above" | "below" = i % 2 === 0 ? "above" : "below";
    const { ribLen, labelHs } = ribInfo[i]!;
    const ribBaseX = SPINE_X1 + 24 + slotStep / 2 + i * slotStep;
    const sign = side === "above" ? -1 : 1;
    const ribEndX = ribBaseX + RIB_COS * ribLen;
    const ribEndY = spineY + sign * RIB_SIN * ribLen;
    const count = b.causes.length;
    const leaves = b.causes.map((cause, index) => {
      const position = (index + 1) / (count + 1);
      return {
        cause,
        lx: ribBaseX + RIB_COS * position * ribLen,
        ly: spineY + sign * RIB_SIN * position * ribLen,
        labelH: labelHs[index]!,
      };
    });
    return {
      ...b,
      side,
      ribBaseX,
      ribLen,
      ribEndX,
      ribEndY,
      palette: paletteFor(b.category, i),
      leaves,
    };
  });

  let maxRightExtent = SPINE_X1 + 220;
  for (const branch of out) {
    maxRightExtent = Math.max(maxRightExtent, branch.ribEndX + PILL_HALF_W);
    for (const leaf of branch.leaves) {
      maxRightExtent = Math.max(maxRightExtent, leaf.lx + labelFootprint);
    }
  }
  const spineX2 = maxRightExtent + 16;
  const headPadX = 22;
  const vbW = spineX2 + headPadX + headW + 24;

  return {
    branches: out,
    spineX1: SPINE_X1,
    spineX2,
    spineY,
    vbW,
    vbH,
    headW,
    headH,
    labelW,
    truncate,
  };
}

/* ------------------------------- body ----------------------------------- */

export function FishboneWidget({
  data,
  compact = true,
  interactive = false,
}: WidgetBodyProps<FishboneParams> & {
  compact?: boolean;
  interactive?: boolean;
}): ReactElement {
  const layout = useMemo(
    () => computeLayout(data.branches, compact),
    [data.branches, compact],
  );
  const [viewport, setViewport] = useState<DiagramViewport>(FIT_VIEWPORT);
  const effectiveViewport = clampViewport(viewport, layout);
  const viewportContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);

  const fitView = useCallback((): void => {
    setViewport(FIT_VIEWPORT);
  }, []);

  const zoomBy = useCallback(
    (delta: number): void => {
      setViewport((current) =>
        clampViewport(
          { ...current, zoom: roundZoom(current.zoom + delta) },
          layout,
        ),
      );
    },
    [layout],
  );

  const handleWheel = useCallback(
    (event: WheelEvent): void => {
      if (!interactive || event.deltaY === 0) return;
      event.preventDefault();
      // Trackpads emit many tiny deltas; do not treat each as a button press.
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? svgRef.current?.clientHeight || 800
            : 1;
      const pixels = Math.max(
        -MAX_WHEEL_PIXELS,
        Math.min(MAX_WHEEL_PIXELS, event.deltaY * unit),
      );
      setViewport((current) =>
        clampViewport(
          { ...current, zoom: current.zoom - pixels * WHEEL_ZOOM_PER_PIXEL },
          layout,
        ),
      );
    },
    [interactive, layout],
  );

  useEffect(() => {
    const svg = svgRef.current;
    if (!interactive || !svg) return;
    svg.addEventListener("wheel", handleWheel, { passive: false });
    return () => svg.removeEventListener("wheel", handleWheel);
  }, [handleWheel, interactive]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<SVGSVGElement>): void => {
      if (!interactive || event.button !== 0) return;
      event.preventDefault();
      viewportContainerRef.current?.focus({ preventScroll: true });
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        panX: effectiveViewport.panX,
        panY: effectiveViewport.panY,
      };
    },
    [effectiveViewport.panX, effectiveViewport.panY, interactive],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<SVGSVGElement>): void => {
      const drag = dragRef.current;
      if (!interactive || !drag || drag.pointerId !== event.pointerId) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const visibleWidth = layout.vbW / effectiveViewport.zoom;
      const visibleHeight = layout.vbH / effectiveViewport.zoom;
      const scale = Math.min(
        bounds.width / visibleWidth,
        bounds.height / visibleHeight,
      );
      if (!Number.isFinite(scale) || scale <= 0) return;

      setViewport(
        clampViewport(
          {
            zoom: effectiveViewport.zoom,
            panX: drag.panX - (event.clientX - drag.startX) / scale,
            panY: drag.panY - (event.clientY - drag.startY) / scale,
          },
          layout,
        ),
      );
    },
    [effectiveViewport.zoom, interactive, layout],
  );

  const stopDragging = useCallback(
    (event: PointerEvent<SVGSVGElement>): void => {
      if (dragRef.current?.pointerId !== event.pointerId) return;
      dragRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (!interactive) return;
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        zoomBy(ZOOM_STEP);
      } else if (event.key === "-") {
        event.preventDefault();
        zoomBy(-ZOOM_STEP);
      } else if (event.key === "0") {
        event.preventDefault();
        fitView();
      }
    },
    [fitView, interactive, zoomBy],
  );

  if (data.branches.length === 0) {
    return (
      <div className="flex-1 grid place-items-center text-pb-text-muted text-xs italic">
        No fishbone data yet — open expanded view and add a category.
      </div>
    );
  }

  const visibleWidth = layout.vbW / effectiveViewport.zoom;
  const visibleHeight = layout.vbH / effectiveViewport.zoom;
  const viewBoxX = (layout.vbW - visibleWidth) / 2 + effectiveViewport.panX;
  const viewBoxY = (layout.vbH - visibleHeight) / 2 + effectiveViewport.panY;
  const zoomPercent = Math.round(effectiveViewport.zoom * 100);

  return (
    // The wrapper is keyboard-operable only in the expanded interactive mode;
    // its conditional role/tabIndex are paired with the same conditional handler.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={viewportContainerRef}
      data-testid={interactive ? "fishbone-interactive-viewport" : undefined}
      role={interactive ? "application" : undefined}
      aria-label={interactive ? "Interactive fishbone diagram" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      className={`flex-1 min-h-0 rounded-md border border-pb-border bg-pb-card-elevated/40 overflow-hidden relative ${
        interactive ? "focus-ring" : ""
      }`}
    >
      {interactive ? (
        <FloatingToolbar
          label="Fishbone zoom"
          surface="glass"
          shape="panel"
          className="absolute end-3 top-3 z-10"
        >
          <Tooltip content="Zoom out">
            <ToolbarIconButton
              aria-label="Zoom out fishbone diagram"
              disabled={effectiveViewport.zoom <= MIN_ZOOM}
              onClick={() => zoomBy(-ZOOM_STEP)}
            >
              <Minus className="h-3.5 w-3.5" aria-hidden="true" />
            </ToolbarIconButton>
          </Tooltip>
          <button
            type="button"
            onClick={fitView}
            aria-label={`Fit fishbone diagram to view (current zoom ${zoomPercent}%)`}
            className="h-7 min-w-11 rounded px-2 text-dense-1 font-medium tabular-nums text-pb-text-muted transition-colors hover:pb-overlay-paint hover:text-pb-text active:pb-overlay-paint-strong focus-ring"
          >
            {zoomPercent}%
          </button>
          <Tooltip content="Zoom in">
            <ToolbarIconButton
              aria-label="Zoom in fishbone diagram"
              disabled={effectiveViewport.zoom >= MAX_ZOOM}
              onClick={() => zoomBy(ZOOM_STEP)}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </ToolbarIconButton>
          </Tooltip>
          <Tooltip content="Fit to view">
            <ToolbarIconButton
              aria-label="Fit fishbone diagram to view"
              onClick={fitView}
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
            </ToolbarIconButton>
          </Tooltip>
        </FloatingToolbar>
      ) : null}
      {/* Kept hand-built (design-system adoption): the fish skeleton — spine,
          ribs, category pills and cause labels — is an SVG diagram (the
          report captures it as a raster), not a control, so no
          design-system part draws its marks or their plain-text hints. */}
      <svg
        ref={svgRef}
        viewBox={`${viewBoxX} ${viewBoxY} ${visibleWidth} ${visibleHeight}`}
        preserveAspectRatio="xMidYMid meet"
        className={`w-full h-full block ${
          interactive ? "cursor-grab active:cursor-grabbing select-none" : ""
        }`}
        role="img"
        aria-label={`Fishbone (Ishikawa) diagram. Effect: ${data.effect}. ${data.branches
          .map(
            (b) =>
              `${b.category}: ${b.causes.length} cause${b.causes.length === 1 ? "" : "s"}`,
          )
          .join("; ")}.`}
        onPointerDown={interactive ? handlePointerDown : undefined}
        onPointerMove={interactive ? handlePointerMove : undefined}
        onPointerUp={interactive ? stopDragging : undefined}
        onPointerCancel={interactive ? stopDragging : undefined}
        style={interactive ? { touchAction: "none" } : undefined}
      >
        <line
          x1={layout.spineX1}
          y1={layout.spineY}
          x2={layout.spineX2}
          y2={layout.spineY}
          stroke="hsl(var(--pb-text-secondary))"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <polygon
          points={`${layout.spineX2},${layout.spineY} ${layout.spineX2 + 16},${layout.spineY - 10} ${layout.spineX2 + 16},${layout.spineY + 10}`}
          fill="hsl(var(--pb-text-secondary))"
        />

        <g>
          <rect
            x={layout.spineX2 + 22}
            y={layout.spineY - layout.headH / 2}
            width={layout.headW}
            height={layout.headH}
            rx={8}
            fill="hsl(var(--pb-amber) / 0.18)"
            stroke="hsl(var(--pb-amber))"
            strokeWidth={1.75}
          />
          <foreignObject
            x={layout.spineX2 + 26}
            y={layout.spineY - layout.headH / 2 + 3}
            width={layout.headW - 8}
            height={layout.headH - 6}
          >
            <div
              className="w-full h-full flex flex-col items-center justify-center text-center px-1"
              style={{ color: "hsl(var(--pb-text))" }}
            >
              <div
                style={{
                  font: "700 9.5px var(--font-sans, system-ui)",
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "hsl(var(--pb-amber))",
                  marginBottom: 2,
                }}
              >
                Effect
              </div>
              <div
                style={{
                  font: "600 11.5px var(--font-sans, system-ui)",
                  lineHeight: 1.2,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
                title={data.effect}
              >
                {data.effect}
              </div>
            </div>
          </foreignObject>
        </g>

        {layout.branches.map((b) => (
          <Rib key={b.id} branch={b} layout={layout} />
        ))}
      </svg>
    </div>
  );
}

/* ------------------------------- rib ------------------------------------ */

function Rib({
  branch,
  layout,
}: {
  branch: LaidOutBranch;
  layout: FishboneLayout;
}): ReactElement {
  const { palette, ribBaseX, ribEndX, ribEndY, side } = branch;

  return (
    <g>
      <line
        x1={ribBaseX}
        y1={layout.spineY}
        x2={ribEndX}
        y2={ribEndY}
        stroke={palette.stroke}
        strokeWidth={1.75}
        strokeLinecap="round"
      />
      <foreignObject
        x={ribEndX - 64}
        y={ribEndY + (side === "above" ? -22 : 4)}
        width={128}
        height={20}
      >
        <div
          className="w-full flex justify-center"
          style={{ pointerEvents: "none" }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 8px",
              borderRadius: 999,
              background: palette.fill,
              border: `1px solid ${palette.stroke}`,
              color: palette.text,
              font: "700 10.5px var(--font-sans, system-ui)",
              letterSpacing: 0.3,
              whiteSpace: "nowrap",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={branch.category}
          >
            {branch.category}
          </span>
        </div>
      </foreignObject>

      {branch.leaves.map(({ cause, lx, ly, labelH }) => {
        const leafEndX = lx + LEAF_LEN;
        const labelX = leafEndX + LABEL_PAD;
        return (
          <g key={cause.id}>
            <line
              x1={lx}
              y1={ly}
              x2={leafEndX}
              y2={ly}
              stroke={palette.stroke}
              strokeOpacity={0.55}
              strokeWidth={1}
            />
            <foreignObject
              x={labelX}
              y={ly - labelH / 2}
              width={layout.labelW}
              height={labelH}
              style={layout.truncate ? undefined : { overflow: "visible" }}
            >
              <div
                className="w-full h-full flex items-center"
                style={{ pointerEvents: "none" }}
              >
                <div
                  className="w-full"
                  style={{
                    color: "hsl(var(--pb-text-secondary))",
                    font: "500 10.5px var(--font-sans, system-ui)",
                    lineHeight: `${LABEL_LINE_H}px`,
                    whiteSpace: layout.truncate ? "nowrap" : "normal",
                    overflow: layout.truncate ? "hidden" : "visible",
                    textOverflow: layout.truncate ? "ellipsis" : undefined,
                    overflowWrap: "break-word",
                    textAlign: "left",
                    pointerEvents: "auto",
                  }}
                  title={cause.text}
                >
                  {cause.text}
                </div>
              </div>
            </foreignObject>
          </g>
        );
      })}
    </g>
  );
}
